import { clamp } from '../core/math';
import type { Rng } from '../core/rng';
import {
  CellState,
  GRID_H,
  GRID_W,
  Grid,
  NEIGHBOURS,
  T,
  TERRAIN,
  idx,
  inBounds,
} from './types';
import type { Difficulty } from './difficulty';
import type { Wind } from './wind';

// Spread tuning. These numbers decide how the game plays, so they are kept
// together and commented: a broad uphill fire edge takes about 2s per cell, flat
// ground about 4s, downhill about 16s, and wind can double that rate or bring it
// almost to a stop.
const EMIT = 0.04;
const SLOPE_UP = 2.2;
const SLOPE_FLAT = 1.0;
const SLOPE_DOWN = 0.68;
/**
 * How much faster fire spreads on the downwind side. Kept moderate on purpose:
 * at 2.0 a strong wind made the fire more or less unstoppable, which is not the
 * game we want. The wind should decide where you fight, not whether you can win.
 */
const WIND_GAIN = 1.35;
/** Heat built up in a cell drains away at this rate, so a cell warmed on its own never catches. */
const HEAT_DECAY = 0.1;
const HEAT_CAP = 3.5;
const DRY_RATE = 0.014;
/**
 * Tries per second, per burning cell, to start a spot fire: a new fire lit by an
 * ember that lands ahead of the flames. Scaled after this by intensity and by
 * wind speed squared.
 */
const SPOT_RATE = 0.02;
/** Moisture above this puts a burning cell out. */
const WET_OUT = 0.5;

export interface FireStats {
  burning: number;
  burnt: number;
  intensity: number;
}

const slopeOf = (dy: number): number => (dy < 0 ? SLOPE_UP : dy > 0 ? SLOPE_DOWN : SLOPE_FLAT);

/** Heat multiplier for spreading in direction (dx, dy) under the given wind. */
export function windWeight(w: Wind, dx: number, dy: number): number {
  const inv = 1 / Math.hypot(dx, dy);
  const dot = (dx * inv) * w.ux + (dy * inv) * w.uy;
  return Math.max(0.08, 1 + w.speed * WIND_GAIN * dot);
}

export function ignite(grid: Grid, i: number): boolean {
  if (grid.state[i] !== CellState.Unburnt) return false;
  if (grid.fuel[i] <= 0.02) return false;
  grid.state[i] = CellState.Burning;
  grid.heat[i] = 0;
  grid.inten[i] = 0.05;
  return true;
}

/** Light a small blob rather than one cell, so a start actually takes hold. */
export function igniteBlob(grid: Grid, centre: number, radius = 1.6): number {
  const cx = centre % GRID_W;
  const cy = Math.floor(centre / GRID_W);
  let lit = 0;
  const r = Math.ceil(radius);
  for (let y = cy - r; y <= cy + r; y++) {
    for (let x = cx - r; x <= cx + r; x++) {
      if (!inBounds(x, y)) continue;
      if (Math.hypot(x - cx, y - cy) > radius) continue;
      if (ignite(grid, idx(x, y))) lit++;
    }
  }
  return lit;
}

export function ignitionThreshold(grid: Grid, i: number): number {
  return TERRAIN[grid.terrain[i]].ign * (1 + grid.moist[i] * 3);
}

/**
 * One fire tick. Two passes, so the result never depends on the order the cells
 * are scanned in: pass one adds up the heat given off by every burning cell,
 * pass two applies it.
 */
export function stepFire(
  grid: Grid,
  wind: Wind,
  diff: Difficulty,
  rng: Rng,
  dt: number,
  onSpot: (from: number, to: number) => void,
): FireStats {
  const heatMul = 1 + diff.ambientHeat * 0.5;
  const dryMul = 1 + diff.ambientHeat * 1.8;
  const { terrain, state, heat, heatAdd, moist, fuel, inten } = grid;

  heatAdd.fill(0);

  // Precompute the eight directional weights once per tick.
  const dirW = NEIGHBOURS.map((n) => n.w * slopeOf(n.dy) * windWeight(wind, n.dx, n.dy) * EMIT * heatMul);

  let burning = 0;
  let burnt = 0;
  let intensitySum = 0;

  // --- Pass 1: emission ----------------------------------------------------
  for (let y = 0; y < GRID_H; y++) {
    const row = y * GRID_W;
    for (let x = 0; x < GRID_W; x++) {
      const i = row + x;
      if (state[i] !== CellState.Burning) {
        if (state[i] === CellState.Burnt) burnt++;
        continue;
      }
      burning++;
      const def = TERRAIN[terrain[i]];
      // Intensity follows a bell curve over the cell's fuel: it builds, peaks,
      // then drops off as the fuel runs out. It starts at a quarter rather than
      // nothing so a new cell still gives off heat.
      const f = def.fuel > 0 ? fuel[i] / def.fuel : 0;
      const it = clamp(Math.min(1, 0.25 + (1 - f) * 3.0) * Math.min(1, f * 3.5), 0.05, 1);
      inten[i] = it;
      intensitySum += it;

      const emit = it * def.emit * dt;
      for (let n = 0; n < 8; n++) {
        const nb = NEIGHBOURS[n];
        const nx = x + nb.dx;
        const ny = y + nb.dy;
        if (nx < 0 || ny < 0 || nx >= GRID_W || ny >= GRID_H) continue;
        const j = ny * GRID_W + nx;
        if (state[j] !== CellState.Unburnt) continue;
        heatAdd[j] += dirW[n] * emit;
      }

      // Embers thrown ahead of the fire edge. This is what gets fire across
      // roads and acequias (the old irrigation channels). Deliberately rare:
      // these spot fires are meant to be a shock, not the main way fire travels.
      if (rng.next() < SPOT_RATE * dt * it * wind.speed * wind.speed) {
        const d = rng.range(4, 14);
        const tx = Math.round(x + wind.ux * d);
        const ty = Math.round(y + wind.uy * d - d * 0.3);
        if (inBounds(tx, ty)) {
          const j = idx(tx, ty);
          if (state[j] === CellState.Unburnt && TERRAIN[terrain[j]].fuel > 0) {
            // Not enough on its own, because a spot fire still has to build up.
            heat[j] += ignitionThreshold(grid, j) * 0.5;
            onSpot(i, j);
          }
        }
      }
    }
  }

  // --- Pass 2: integrate ---------------------------------------------------
  for (let i = 0; i < state.length; i++) {
    const def = TERRAIN[terrain[i]];

    if (state[i] === CellState.Burning) {
      fuel[i] -= def.burn * dt * heatMul * (0.4 + inten[i] * 0.8);
      moist[i] = Math.max(0, moist[i] - dt * 0.35);
      if (fuel[i] <= 0) {
        fuel[i] = 0;
        state[i] = CellState.Burnt;
        inten[i] = 0;
      } else if (moist[i] > WET_OUT) {
        state[i] = CellState.Unburnt;
        inten[i] = 0;
        heat[i] = 0;
      }
      continue;
    }

    if (def.fuel <= 0) {
      heat[i] = 0;
      continue;
    }

    // Ground that has been soaked by a drop gives up its water far more slowly
    // than ground that is only damp, so a wet line laid ahead of the fire stays
    // wet for the best part of a minute. Hot air still dries it out.
    if (moist[i] > def.moist * 0.35) {
      const rate = DRY_RATE * dryMul * (moist[i] > def.moist ? 0.35 : 1);
      moist[i] = Math.max(def.moist * 0.35, moist[i] - rate * dt);
    }

    if (state[i] === CellState.Burnt) {
      heat[i] = 0;
      continue;
    }

    let h = heat[i] + heatAdd[i] - heat[i] * HEAT_DECAY * dt;
    if (h < 0) h = 0;
    else if (h > HEAT_CAP) h = HEAT_CAP;
    heat[i] = h;

    if (h >= ignitionThreshold(grid, i)) {
      state[i] = CellState.Burning;
      heat[i] = 0;
      inten[i] = 0.05;
    }
  }

  return { burning, burnt, intensity: intensitySum };
}

/** A water drop landing at cell (cx, cy). It wets the ground, cools it and puts out flames. */
export function applyWater(grid: Grid, cx: number, cy: number, radius: number, amount: number): number {
  const r = Math.ceil(radius);
  let doused = 0;
  for (let y = cy - r; y <= cy + r; y++) {
    for (let x = cx - r; x <= cx + r; x++) {
      if (!inBounds(x, y)) continue;
      const d = Math.hypot(x - cx, y - cy);
      if (d > radius) continue;
      const i = idx(x, y);
      if (grid.terrain[i] === T.Sky || grid.terrain[i] === T.Water) continue;
      const falloff = 1 - (d / radius) * 0.75;
      grid.moist[i] = Math.min(1.2, grid.moist[i] + amount * falloff);
      grid.heat[i] *= 0.15;
      if (grid.state[i] === CellState.Burning && grid.moist[i] > WET_OUT) {
        grid.state[i] = CellState.Unburnt;
        grid.inten[i] = 0;
        doused++;
      }
    }
  }
  return doused;
}

/** Fire danger at a cell, used by pathfinding and by people deciding to flee. */
export function danger(grid: Grid, x: number, y: number): number {
  if (!inBounds(x, y)) return 0;
  const i = idx(x, y);
  if (grid.state[i] === CellState.Burning) return 1;
  return clamp(grid.heat[i] / Math.max(0.05, ignitionThreshold(grid, i)), 0, 0.95);
}
